/**
 * @property {string} key
 * @property {object} precursors
 */
export class FieldEntry {
    /**
     * Create a field entry for the provided key
     * @param {string} key
     */
    constructor({key} = {}) {
        this.key = key;
        this.precursors = {};
    }

    /**
     * Add a precursor entry for the provided path by appending the key
     * @param {string} path
     */
    addPrecursor(path) {
        // TODO make delimiter a property
        this.precursors[path] = `${path}/${this.key}`;
    }
}

/**
 * @property {string} precursor
 * @property {object} keys
 */
export class PrecursorEntry {
    /**
     * Create a precursor entry for the provided value
     * @param {string} precursor
     */
    constructor({precursor} = {}) {
        this.precursor = precursor;
        this.keys = {};
    }

    /**
     * Add a key to the list of existing matches for this precursor
     * @param {string} name
     * @param {{}} field
     */
    addKey(name, field) {
        this.keys[name] = field;
    }
}

/**
 * @property {number} perfectMatchWeight
 * @property {number} partialMatchWeight
 * @property {{}.<FieldEntry>} fieldIndex
 * @property {{}.<PrecursorEntry>} precursorIndex
 */
export default class SearchProvider {
    /**
     * Instantiate the search provider and build the index from the groups
     * @param {FieldProvider} fieldProvider
     */
    constructor({fieldProvider} = {}) {
        this.perfectMatchWeight = 4;
        this.partialMatchWeight = 1;
        this.fieldIndex = {};
        this.precursorIndex = {};
        this.buildIndexes(fieldProvider.fieldGroups);
    }

    /**
     * Iterate through the values in the groups and index them
     * @param {{}} groups
     */
    buildIndexes(groups) {
        Object.values(groups)
            .forEach(group => this.indexGroup(group));
    }

    /**
     * Index a group recursively by traversing all fields within to
     * the lowest point in the tree
     * @param {{}} fields
     * @param {string} path
     */
    indexGroup({fields, path}) {
        /**
         * Recursive handler for groups
         * @param {{}} definition
         * @param {string} ancestor
         * @param {{}?} flags
         */
        // special check for arrays of arrays (e.g. 'invoices')
        const parseRecursive = ({definition, ancestor, flags = {}}) => {
            Object.keys(definition)
                .forEach((key) => {
                    const field = definition[key];
                    if (field.$array) {
                        if (field.$nested) {
                            parseRecursive({
                                definition: SearchProvider.popNestedArray(field),
                                ancestor: `${ancestor}/${key}`,
                                flags: {$nestedArray: true},
                            });
                        } else {
                            this.addToGroupIndex({
                                key,
                                precursor: ancestor,
                                field,
                                flags: {$array: true},
                            });
                        }
                    } else if (field.$nested) {
                        parseRecursive({
                            definition: field,
                            ancestor: `${ancestor}/${key}`,
                            flags,
                        });
                    } else if (!/^\$/.test(key)) {
                        this.addToGroupIndex({
                            key,
                            precursor: ancestor,
                            field,
                            flags,
                        });
                    }
                });
        };
        parseRecursive({
            definition: fields,
            ancestor: path,
        });
    }

    /**
     * Add a key to the precursor. If the precursor doesn't exist then create it.
     * @param {string} key
     * @param {string} precursor
     * @param {{}?} flags
     * @param {{}} field
     */
    addToGroupIndex({
        key, precursor, field, flags = {},
    }) {
        if (!Reflect.has(this.fieldIndex, key)) {
            this.fieldIndex[key] = new FieldEntry({key});
        }
        this.fieldIndex[key].addPrecursor(precursor);
        if (!Reflect.has(this.precursorIndex, precursor)) {
            this.precursorIndex[precursor] = new PrecursorEntry({precursor});
        }
        this.precursorIndex[precursor].addKey(key, {
            ...field,
            $nestedArray: flags.$nestedArray,
            $array: flags.$array,
        });
    }

    /**
     * Copy a node that represents and array of fields and remove the internal flags
     * @param {{}} node
     * @returns {{}}
     */
    static popNestedArray(node) {
        const copy = {...node};
        delete copy.$nested;
        delete copy.$array;
        return copy;
    }

    /**
     * Find the provided query in both the key and precursor indexes and sort the results by
     * relevance
     * @param {string?} rawQuery
     * @return {Array}
     */
    find(rawQuery = '') {
        const words = rawQuery.trim()
            .split(/\s/)
            .filter(word => word !== '');
        if (words.length) {
            const results = {};
            words.forEach((word) => {
                this.lookupKey(word, results);
                this.lookupPrecursors(word, results);
            });
            return SearchProvider.sortByRelevance(results);
        }
        return [];
    }

    /**
     * Compare two weights and two strings. First return the descending position
     * of the weights. If the weights are equal sort on the alphabetical position
     * of the strings.
     * @param aWeight
     * @param bWeight
     * @param aWord
     * @param bWord
     * @return {number}
     */
    static determinePairPosition(aWeight, bWeight, aWord, bWord) {
        // sort weights descending
        const diff = bWeight - aWeight;
        if (diff === 0) {
            // sort same weights by words alphabetically
            if (aWord < bWord) {
                return -1;
            }
            if (aWord > bWord) {
                return 1;
            }
        }
        return diff;
    }

    /**
     * Sort the results by relevance. First compare the weights of all precursors and sort equal
     * ones alphabetically. Then sort the keys within precursors using the same method.
     * @param {{}} results
     * @return {Array}
     */
    static sortByRelevance(results) {
        const keys = Object.keys(results);
        if (keys.length) {
            let maxPrecursorWeight = 0;
            keys.sort((pathA, pathB) => {
                const {$precursorWeight: aWeight} = results[pathA];
                const {$precursorWeight: bWeight} = results[pathB];
                const max = Math.max(aWeight, bWeight);
                if (max > maxPrecursorWeight) {
                    maxPrecursorWeight = max;
                }
                return SearchProvider.determinePairPosition(aWeight, bWeight, pathA, pathB);
            });
            /**
             * Cache reducer executor method to speed up computation
             * @type {(function(*, *): void)|(function(*): *)}
             */
            const executor = (() => {
                if (maxPrecursorWeight > 0) {
                    return (callback, weight) => {
                        // calculate distance between precursor and the
                        // maximum known weight; if the distance is greater
                        // than `1` then we do not include it in the results
                        const distance = maxPrecursorWeight - weight;
                        if (distance <= 1) {
                            callback();
                        }
                    };
                }
                return callback => callback();
            })();
            return keys.reduce((acc, step) => {
                const {$precursorWeight = 0, ...fields} = results[step];
                const addGroup = () => {
                    acc.push({
                        fullPath: step,
                        fields: SearchProvider.sortFieldsByWeight(fields),
                    });
                };
                executor(addGroup, $precursorWeight);
                return acc;
            }, []);
        }
        return [];
    }

    /**
     * Sort the keys within the fields list by their weight and when equal by their names
     * @param {{}} fields
     * @return {string[]}
     */
    static sortFieldsByWeight(fields) {
        return Object.keys(fields)
            .sort((keyA, keyB) => SearchProvider
                .determinePairPosition(fields[keyA], fields[keyB], keyA, keyB));
    }

    /**
     * Lookup the provided word in the field index and add results to the accumulator
     * @param {string} word
     * @param {{}.<String>} accumulator
     */
    lookupKey(word, accumulator) {
        Object.keys(this.fieldIndex)
            .forEach((field) => {
                if (word === field) {
                    this.addKeyMatch(field, accumulator, this.perfectMatchWeight);
                } else {
                    let pattern = new RegExp(word, 'gi');
                    if (pattern.test(field)) {
                        this.addKeyMatch(field, accumulator, this.partialMatchWeight);
                        pattern = null;
                    }
                }
            });
    }

    /**
     * Add an entry for the word as a key int he accumulator
     * @param {string} word
     * @param {{}} accumulator
     * @param {number} weight
     */
    addKeyMatch(word, accumulator, weight) {
        const {key, precursors} = this.fieldIndex[word];
        Object.keys(precursors)
            .forEach((path) => {
                SearchProvider.addPrecursorResult({
                    accumulator,
                    path,
                    key,
                    weight,
                });
            });
    }

    /**
     * Add a precursor result to the accumulator and increment its weight
     * @param {{}} accumulator
     * @param {string} path
     * @param {string} key
     * @param {number} weight
     */
    static addPrecursorResult({
        accumulator, path, key, weight,
    }) {
        SearchProvider.preparePrecursorResult({
            accumulator,
            key,
            path,
        });
        accumulator[path][key] += weight;
    }

    /**
     * Increase the weight of the precursor path
     * @param {{}} accumulator
     * @param {string} path
     */
    static increasePrecursorWeight({accumulator, path}) {
        SearchProvider.preparePrecursorResult({
            accumulator,
            path,
        });
        accumulator[path].$precursorWeight += 1;
    }

    /**
     * Prepare an entry for the precursor in the accumulator before adding keys to it
     * in future steps
     * @param {{}} accumulator
     * @param {string} path
     * @param {string} key
     * @param {number} defaultWeight
     */
    static preparePrecursorResult({
        accumulator, path, key, defaultWeight = 0,
    }) {
        if (!Reflect.has(accumulator, path)) {
            accumulator[path] = {$precursorWeight: 0};
        }
        if (key && !Reflect.has(accumulator[path], key)) {
            accumulator[path][key] = defaultWeight;
        }
    }

    /**
     * Lookup the word in the precursor index and add it if it matches
     * @param {string} word
     * @param {{}} accumulator
     */
    lookupPrecursors(word, accumulator) {
        Object.keys(this.precursorIndex)
            .forEach((path) => {
                let pattern = new RegExp(word, 'gi');
                if (pattern.test(path)) {
                    SearchProvider.increasePrecursorWeight({accumulator, path});
                    this.addPrecursorMatch({
                        word,
                        accumulator,
                        path,
                    });
                    // force garbage collect
                    pattern = null;
                }
            });
    }

    /**
     * Add a precursor match to the accumulator and include all keys found in the index
     * entry. If the key is a perfect match increase the weight by an extra point.
     * @param {string} word
     * @param {{}} accumulator
     * @param {string} path
     */
    addPrecursorMatch({
        word, accumulator, path,
    }) {
        const {keys, precursor} = this.precursorIndex[path];
        Object.keys(keys)
            .forEach((key) => {
                let isMatch = new RegExp(word, 'gi').test(key);
                SearchProvider.addPrecursorResult({
                    accumulator,
                    path: precursor,
                    key,
                    weight: isMatch ? 2 : 1,
                });
                isMatch = null;
            });
    }

    getFieldDefinition({field, precursor}) {
        if (Reflect.has(this.precursorIndex, precursor)
            && Reflect.has(this.precursorIndex[precursor].keys, field)) {
            return {
                field,
                precursor,
                definition: this.precursorIndex[precursor].keys[field],
            };
        }
        return null;
    }
}
